
import { defineComponent } from 'vue';
import { IDinhMucCoDinhNewResponseBase } from '@/core/interfaces/ApiResponses';
import { DinhMucCoDinhService } from '@/core/services/DinhMucCoDinh.service';
import { swalErrNotification } from '@/core/helpers/utils';
import { formatDisplayingDate } from '@/core/hooks/use-date-time-hook';
import { generateTablePdf } from '@/core/helpers/generate-table-pdf';
import PdfViewerPage from '@/components/shared/pdf-viewer/PdfViewerPage.vue';
import { PDF_GENERATE_TABLE_DEFAULTS } from '@/components/shared/pdf-viewer/pdf-content.config';
import { pickDonViPdfExtra } from '@/components/shared/pdf-viewer/pdf-document-header';

const DINH_MUC_CO_DINH_PDF_COLUMNS = [
  { header: 'STT', width: 18, align: 'center' as const },
  { header: 'Số văn bản', width: 35, align: 'left' as const },
  { header: 'Cấp S/C', width: 55, align: 'left' as const },
  { header: 'Nhóm toa xe', width: 40, align: 'left' as const },
  { header: 'Ban hành', width: 28, align: 'center' as const },
  { header: 'Hiệu lực', width: 28, align: 'center' as const },
  { header: 'Hết hiệu lực', width: 32, align: 'center' as const },
];

export default defineComponent({
  name: 'DinhMucCoDinhListViewer',

  components: { PdfViewerPage },

  data() {
    return {
      items: [] as IDinhMucCoDinhNewResponseBase[],
      tenCapSc: '',
      tenDmToaXe: '',
      pdfSource: null as string | null,
      isGenerating: false,
      tenDonVi: '',
      tenDonViCha: '',
    };
  },

  computed: {
    pdfFileName(): string {
      return 'danh-sach-dinh-muc-co-dinh.pdf';
    },

    subtitle(): string | undefined {
      const parts: string[] = [];
      if (this.tenCapSc) parts.push(`Cấp S/C: ${this.tenCapSc}`);
      if (this.tenDmToaXe) parts.push(`Nhóm toa xe: ${this.tenDmToaXe}`);
      return parts.length ? parts.join(' · ') : undefined;
    },
  },

  methods: {
    queryString(key: string): string {
      const value = this.$route.query[key];
      return typeof value === 'string' ? value : '';
    },

    queryNumber(key: string): number | undefined {
      const raw = this.queryString(key);
      if (!raw) return undefined;
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : undefined;
    },

    async loadAndGenerate() {
      const idCapSc = this.queryNumber('idCapSc');
      const idDmToaXe = this.queryNumber('idDmToaXe');
      this.tenCapSc = this.queryString('tenCapSc');
      this.tenDmToaXe = this.queryString('tenDm');

      try {
        this.isGenerating = true;
        this.pdfSource = null;

        const listResult = await DinhMucCoDinhService.listAll({
          ...(idCapSc && { id_cap_sc: idCapSc }),
          ...(idDmToaXe && { id_dm_toa_xe: idDmToaXe }),
        });
        this.items = listResult.items;
        const { tenDonVi, tenDonViCha } = pickDonViPdfExtra(listResult.extra);
        this.tenDonVi = tenDonVi;
        this.tenDonViCha = tenDonViCha;
        this.pdfSource = await this.generatePdf();
      } catch (e) {
        swalErrNotification(e, 'Có lỗi khi tải danh sách định mức cố định');
      } finally {
        this.isGenerating = false;
      }
    },

    async generatePdf(): Promise<string> {
      const rows = this.items.map((item, index) => [
        String(index + 1),
        item.so_bien_ban || item.so_van_ban || '-',
        item.cap_sua_chua?.ten_cap_sc || '-',
        item.danh_muc_toa_xe?.ten_danh_muc || '-',
        formatDisplayingDate(item.ngay_ban_hanh),
        formatDisplayingDate(item.ngay_hieu_luc),
        item.ngay_het_han ? formatDisplayingDate(item.ngay_het_han) : 'Chưa xác định',
      ]);

      return generateTablePdf({
        ...PDF_GENERATE_TABLE_DEFAULTS,
        title: 'DANH SÁCH ĐỊNH MỨC CỐ ĐỊNH',
        subtitle: this.subtitle,
        columns: DINH_MUC_CO_DINH_PDF_COLUMNS,
        rows,
        documentHeader: {
          tenDonVi: this.tenDonVi,
          tenDonViCha: this.tenDonViCha,
          variant: 'masterList',
        },
      });
    },
  },

  mounted() {
    this.loadAndGenerate();
  },
});
